import React, { useMemo, useState } from 'react';
import { useApp } from '@/contexts/AppContext';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import AnimatedCounter from '@/components/apex/AnimatedCounter';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import HowCalculated from '@/components/apex/HowCalculated';
import RecordDrawer from '@/components/apex/RecordDrawer';
import { ArrowDownAZ, ArrowDownNarrowWide, ArrowUpNarrowWide } from 'lucide-react';

export default function GovernanceDebt() {
  const { seed } = useApp();
  const navigate = useNavigate();
  const [view, setView] = useState('usd');
  const [sortBy, setSortBy] = useState('chronological');
  const [activeCat, setActiveCat] = useState(null);
  const [activeFwk, setActiveFwk] = useState(null);

  const sorted12m = useMemo(() => {
    if (!seed) return [];
    const data = seed.governance_debt.trend_12m.map((m) => ({ ...m, value_k: m.value / 1000 }));
    if (sortBy === 'asc') return [...data].sort((a, b) => a.value - b.value);
    if (sortBy === 'desc') return [...data].sort((a, b) => b.value - a.value);
    return data;
  }, [seed, sortBy]);

  if (!seed) return null;
  const d = seed.governance_debt;

  const buildCategoryRecord = (c) => ({
    idTag: c.name,
    title: c.name,
    severity: c.amount_usd > 250000 ? 'high' : c.amount_usd > 100000 ? 'medium' : 'low',
    subtitle: c.detail,
    meta: [
      { label: 'Current debt', value: `$${(c.amount_usd / 1000).toFixed(0)}K`, mono: true, tone: 'critical' },
      { label: 'Share of total', value: `${((c.amount_usd / d.total_usd) * 100).toFixed(1)}%`, mono: true },
      { label: 'Recent reduction', value: `−$${Math.round(c.amount_usd * 0.18 / 1000)}K`, mono: true, tone: 'success' },
      { label: 'Trend', value: '↓ 18% QoQ', tone: 'success' },
    ],
    sections: [
      {
        title: 'Why this is debt',
        type: 'paragraph',
        body: `${c.detail}. APEX prices each contributor by combining exposure duration, data sensitivity, observed activity, and the actor's privilege scope. The number represents what an auditor or insurer would treat as accumulated liability today.`,
      },
      {
        title: 'How to reduce it fast',
        type: 'list',
        items: [
          'Open the Remediation Center and start with the highest debt-per-minute action',
          'Investigations linked to this category will auto-close their contribution once verified',
          'Schedule a recurring review so this category does not silently accumulate again',
        ],
      },
    ],
    actions: [
      { label: 'Open Remediation', onClick: () => navigate('/lens/remediation'), primary: true },
      { label: 'Open Investigations', onClick: () => navigate('/lens/investigations') },
    ],
  });

  const buildFrameworkLiabilityRecord = (f) => ({
    idTag: f.name,
    title: `${f.name} liability`,
    severity: f.amount_usd > 200000 ? 'high' : 'medium',
    subtitle: 'Debt attributable to gaps in this framework. Closing investigations linked to the framework reduces this directly.',
    meta: [
      { label: 'Current debt', value: `$${(f.amount_usd / 1000).toFixed(0)}K`, mono: true, tone: 'critical' },
      { label: 'Share of total', value: `${((f.amount_usd / d.total_usd) * 100).toFixed(1)}%`, mono: true },
      { label: 'Open investigations', value: '2', mono: true },
      { label: 'Audit-ready packets', value: '3 in vault', mono: true, tone: 'success' },
    ],
    sections: [
      {
        title: 'What this framework is exposed to',
        type: 'list',
        items: [
          'Active control gaps with no current signed evidence packet',
          'Recurring exceptions that exceed the documented risk-acceptance window',
          'Cross-framework overlaps (one open gap can contribute to multiple frameworks)',
        ],
      },
      {
        title: 'Path to zero',
        type: 'paragraph',
        body: `${f.name} liability drops automatically as APEX assembles fresh evidence and as linked investigations close. Most teams reach zero within one quarter once the audit workspace is wired in.`,
      },
    ],
    actions: [
      { label: 'Open Audit Workspace', onClick: () => navigate('/lens/audit'), primary: true },
      { label: 'Open Evidence Vault', onClick: () => navigate('/lens/evidence') },
    ],
  });

  return (
    <div className="space-y-6" data-testid="governance-debt">
      <div>
        <span className="text-[10px] uppercase tracking-[0.18em] text-apex-lens font-semibold">Governance · Debt Ledger</span>
        <h1 className="text-2xl font-bold tracking-tight mt-1">Quantified governance liability</h1>
      </div>

      {/* Hero */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 lg:p-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-apex-neutral mb-1">Current Governance Debt</div>
            {view === 'usd' && (
              <div key="usd" data-testid="debt-hero-usd" className="text-5xl sm:text-6xl font-extrabold tabular text-apex-ink tracking-tight">
                $<AnimatedCounter value={d.total_usd / 1000} duration={1.4} />K
              </div>
            )}
            {view === 'score' && (
              <div key="score" data-testid="debt-hero-score" className="text-5xl sm:text-6xl font-extrabold tabular text-apex-ink tracking-tight">
                <AnimatedCounter value={Math.round((1 - d.total_usd / d.benchmark_usd) * 100 + 40)} duration={1.2} />
                <span className="text-2xl font-semibold text-apex-neutral ml-2">/ 100</span>
              </div>
            )}
            {view === 'hours' && (
              <div key="hours" data-testid="debt-hero-hours" className="text-5xl sm:text-6xl font-extrabold tabular text-apex-ink tracking-tight">
                <AnimatedCounter value={d.avoidable_hours_qtr} duration={1.2} />
                <span className="text-2xl font-semibold text-apex-neutral ml-1">hrs/qtr</span>
              </div>
            )}
            {view === 'usd' && <div className="text-sm text-apex-success font-mono mt-1">↓ {Math.abs(d.trend_pct_qtr)}% (−${(d.reduction_usd_qtr / 1000).toFixed(0)}K) this quarter</div>}
            {view === 'score' && <div className="text-sm text-apex-success font-mono mt-1">↑ Governance maturity score · 40% better than benchmark</div>}
            {view === 'hours' && <div className="text-sm text-apex-success font-mono mt-1">340 hours of avoidable manual work this quarter · at $150/hr = $51K</div>}
          </div>
          <Tabs value={view} onValueChange={setView}>
            <TabsList>
              <TabsTrigger value="usd" data-testid="debt-tab-usd">$ Currency</TabsTrigger>
              <TabsTrigger value="score" data-testid="debt-tab-score">Indexed Score</TabsTrigger>
              <TabsTrigger value="hours" data-testid="debt-tab-hours">Hours</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <p className="text-xs text-apex-neutral mt-5 leading-relaxed max-w-3xl">
          Governance debt represents the accumulated risk liability from control failures, overpermissioned access, contaminated environments, and missing evidence. It is not a prediction — it is derived from immutable records of what actually happened. Industry benchmark for organizations of this size: ${(d.benchmark_usd / 1000000).toFixed(1)}M. Your debt is 40% below benchmark.
        </p>
      </div>

      {view === 'hours' && (
        <HowCalculated storageKey="hours-view" testId="how-calculated-hours">
          <p className="mb-3 text-[11px] text-apex-neutral">
            Avoidable governance work is the manual effort your team currently spends on tasks
            APEX Lens would eliminate or accelerate. Calculated at $150/hr industry average.
          </p>
          <div className="space-y-1.5 font-mono text-[11px] border-t border-slate-100 pt-3">
            <div className="flex justify-between gap-4"><span className="text-apex-neutral">Manual evidence assembly for audits</span><span className="text-apex-ink">140 hrs · $21,000</span></div>
            <div className="flex justify-between gap-4"><span className="text-apex-neutral">IAM access reviews without behavioral data</span><span className="text-apex-ink">80 hrs · $12,000</span></div>
            <div className="flex justify-between gap-4"><span className="text-apex-neutral">Container finding triage (127K findings)</span><span className="text-apex-ink">60 hrs · $9,000</span></div>
            <div className="flex justify-between gap-4"><span className="text-apex-neutral">Incident reconstruction without timeline</span><span className="text-apex-ink">40 hrs · $6,000</span></div>
            <div className="flex justify-between gap-4"><span className="text-apex-neutral">Board report preparation</span><span className="text-apex-ink">20 hrs · $3,000</span></div>
            <div className="flex justify-between gap-4 border-t border-slate-200 pt-2 mt-1 font-semibold"><span className="text-apex-neutral">Total avoidable work</span><span className="text-apex-success">340 hrs · $51,000/qtr</span></div>
          </div>
        </HowCalculated>
      )}

      <HowCalculated storageKey="governance-debt" testId="how-calculated-debt">
        <p className="mb-2 font-semibold">
          Formula: Control Gap Severity × Asset Criticality × Exposure Duration
          × Data Sensitivity × Observed Activity × Recurrence Rate
        </p>
        <p className="mb-3 text-apex-neutral text-[11px]">          Each factor is derived from immutable CloudTrail and Config records.
          This is not a prediction or model output. It is a calculation from
          verified cloud telemetry.
        </p>
        <div className="space-y-1.5 font-mono text-[11px] border-t border-slate-100 pt-3">
          <div className="flex justify-between gap-4">
            <span className="text-apex-neutral">Identity Debt</span>
            <span className="text-apex-ink">$312,000 · 47 overpermissioned roles</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-apex-neutral">Control Gap Debt</span>
            <span className="text-apex-ink">$287,000 · 3 active gaps · 11 resolved</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-apex-neutral">Container Debt</span>
            <span className="text-apex-ink">$164,000 · 2 dev-origin images · 312 dormant</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-apex-neutral">Missing Evidence</span>
            <span className="text-apex-ink">$84,000 · 4 active findings</span>
          </div>
        </div>
        <p className="mt-3 text-[11px] text-apex-neutral border-t border-slate-100 pt-3">
          Industry benchmark: $2.1M average for organizations of this size and
          compliance profile. Your debt is 40% below benchmark.
        </p>
      </HowCalculated>

      {/* Governance velocity — rate of change */}
      <div className="bg-slate-50 rounded-lg border border-slate-200 p-4" data-testid="governance-velocity">
        <div className="text-[10px] uppercase tracking-wider font-mono text-apex-neutral mb-3">
          Governance velocity — rate of change
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Debt reduction rate',    value: '↓ $21K/week',     sub: 'Improving · remediation active',     tone: 'text-apex-success', testId: 'velocity-debt' },
            { label: 'Contamination velocity', value: '↓ 1.2 pts/week',  sub: 'Score improving · 23 → target 12',   tone: 'text-apex-success', testId: 'velocity-contamination' },
            { label: 'New findings rate',      value: '↑ 2.3/week',      sub: 'New gaps emerging · watch trend',    tone: 'text-apex-warning', testId: 'velocity-findings' },
            { label: 'Remediation velocity',   value: '↑ 4.1 actions/week', sub: 'Outpacing new findings',          tone: 'text-apex-success', testId: 'velocity-remediation' },
          ].map((v) => (
            <div key={v.label} data-testid={v.testId} className="bg-white rounded-lg border border-slate-200 p-3">
              <div className="text-[10px] text-apex-neutral font-mono mb-1">{v.label}</div>
              <div className={`text-[15px] font-bold tabular-nums font-mono ${v.tone}`}>{v.value}</div>
              <div className="text-[10px] text-apex-neutral mt-0.5">{v.sub}</div>
            </div>
          ))}
        </div>
        <div className="mt-2 text-[10px] font-mono text-slate-400">
          Velocity measured over rolling 30-day window from CloudTrail-derived governance fact history
        </div>
      </div>

      {/* Categories */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {d.categories.map((c, i) => (
          <motion.button
            key={c.name}
            type="button"
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}
            onClick={() => setActiveCat(c)}
            data-testid={`debt-category-${c.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}
            className="bg-white rounded-lg border border-slate-200 p-4 text-left hover:shadow-sm hover:-translate-y-0.5 hover:border-apex-lens transition-all"
          >
            <div className="text-[10px] uppercase tracking-wider text-apex-neutral font-semibold">{c.name}</div>
            <div className="text-2xl font-bold tabular text-apex-ink mt-1">$<AnimatedCounter value={c.amount_usd / 1000} />K</div>
            <p className="text-[11px] text-apex-neutral mt-2 leading-relaxed">{c.detail}</p>
          </motion.button>
        ))}
      </div>

      {/* Trend chart */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h3 className="text-sm font-bold text-apex-ink">12-Month Trend</h3>
          <Tabs value={sortBy} onValueChange={setSortBy} data-testid="debt-trend-sort">
            <TabsList>
              <TabsTrigger value="chronological" data-testid="debt-sort-chrono"><ArrowDownAZ className="w-3 h-3 mr-1" />Chronological</TabsTrigger>
              <TabsTrigger value="desc" data-testid="debt-sort-desc"><ArrowDownNarrowWide className="w-3 h-3 mr-1" />Highest first</TabsTrigger>
              <TabsTrigger value="asc" data-testid="debt-sort-asc"><ArrowUpNarrowWide className="w-3 h-3 mr-1" />Lowest first</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <div className="h-64 w-full min-h-[256px]">
          <ResponsiveContainer width="100%" height="100%" minWidth={0}>
            <AreaChart data={sorted12m}>
              <defs>
                <linearGradient id="debtFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0D9488" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#0D9488" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748B' }} stroke="#E2E8F0" />
              <YAxis tick={{ fontSize: 11, fill: '#64748B' }} stroke="#E2E8F0" tickFormatter={(v) => `$${v}K`} />
              <Tooltip
                contentStyle={{ backgroundColor: '#0F172A', border: '1px solid #1E293B', borderRadius: 8, color: 'white' }}
                labelStyle={{ color: '#94A3B8', fontSize: 11 }}
                formatter={(v) => [`$${Number(v).toLocaleString()}K`, 'Debt']}
              />
              <Area type="monotone" dataKey="value_k" stroke="#0D9488" strokeWidth={2} fill="url(#debtFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 text-[11px] font-mono">
          {d.annotations.map((a) => (
            <div key={a.month} className="text-apex-success">• {a.month}: {a.label}</div>
          ))}
        </div>
      </div>

      {/* Framework liability */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="text-sm font-bold text-apex-ink mb-4">Framework Liability Breakdown</h3>
        <div className="space-y-3">
          {d.framework_liability.map((f, i) => {
            const pct = (f.amount_usd / d.total_usd) * 100;
            return (
              <button
                key={f.name}
                type="button"
                onClick={() => setActiveFwk(f)}
                data-testid={`debt-framework-${f.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                className="w-full text-left rounded-md p-2 -mx-2 hover:bg-slate-50 transition-colors"
              >
                <div className="flex justify-between text-xs mb-1">
                  <span className="font-semibold">{f.name}</span>
                  <span className="font-mono tabular">${(f.amount_usd / 1000).toFixed(0)}K</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.9, delay: i * 0.1 }}
                    className="h-full bg-apex-lens"
                  />
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Avoidable hours */}
      <div className="bg-apex-shell text-white rounded-xl p-5 grain">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400">Avoidable governance work</div>
            <div className="text-2xl font-bold tabular mt-0.5"><AnimatedCounter value={d.avoidable_hours_qtr} /> hours / quarter</div>
            <div className="text-xs text-slate-400 mt-1">At ${d.hourly_rate_usd}/hr · ${(d.avoidable_hours_qtr * d.hourly_rate_usd / 1000).toFixed(0)}K in manual effort eliminable</div>
          </div>
          <button
            onClick={() => navigate('/lens/remediation')}
            data-testid="debt-open-remediation"
            className="text-xs font-semibold text-apex-lens hover:text-white transition-colors inline-flex items-center gap-1 px-3 py-2 rounded-md border border-apex-lens hover:bg-apex-lens"
          >
            Open remediation plan →
          </button>
        </div>
      </div>

      <RecordDrawer
        open={!!activeCat}
        onOpenChange={(o) => !o && setActiveCat(null)}
        record={activeCat ? buildCategoryRecord(activeCat) : null}
        testId="debt-category-drawer"
      />
      <RecordDrawer
        open={!!activeFwk}
        onOpenChange={(o) => !o && setActiveFwk(null)}
        record={activeFwk ? buildFrameworkLiabilityRecord(activeFwk) : null}
        testId="debt-framework-drawer"
      />
    </div>
  );
}
